import { MapMode, MapTool } from '../map/map-types';

/**
 * The Suburbs workspace's keyboard shortcuts (#256, #260), as a pure mapping from a key press to a command; the
 * workspace runs the command. Tested in workspace-keys.spec.ts.
 *
 *   Esc        close the side panel (a lasso in progress takes it first)
 *   Cmd/Ctrl+Z undo the last map change
 *   1–9        make that region active
 *   S A E R    View, Assign, Erase (again: back to View), Sub-region mode
 *   X          Sub-region mode: switch Add / Remove
 *   O U N      sub-region outlines, unassigned only, suburb names
 *   G          Grow
 *   V L P      Pointer, Lasso, Polygon
 */

/** The map's Show checkboxes. */
export type MapFilter = 'labels' | 'unassigned' | 'subregions';

export type KeyCommand =
  | { kind: 'escape' }
  | { kind: 'undo' }
  | { kind: 'region'; index: number }
  | { kind: 'mode'; mode: MapMode }
  | { kind: 'subregion-action' }
  | { kind: 'filter'; filter: MapFilter }
  | { kind: 'grow' }
  | { kind: 'tool'; tool: MapTool };

export interface KeyContext {
  /** Focus is in a field, or anywhere in the side panel's body: only Esc counts. */
  typing: boolean;
  /** The map is showing (not the List): the map's own keys count. */
  onMap: boolean;
  mode: MapMode;
  regionCount: number;
}

export interface KeyPress {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
}

const MODE_KEYS = new Map<string, MapMode>([
  ['s', 'view'],
  ['a', 'assign'],
  ['r', 'subregion'],
]);
const FILTER_KEYS = new Map<string, MapFilter>([
  ['o', 'subregions'],
  ['u', 'unassigned'],
  ['n', 'labels'],
]);
const TOOL_KEYS = new Map<string, MapTool>([
  ['v', 'pointer'],
  ['l', 'lasso'],
  ['p', 'polygon'],
]);
const MAX_REGION_KEY = 9;

export function keyCommand(e: KeyPress, ctx: KeyContext): KeyCommand | null {
  if (e.key === 'Escape') return { kind: 'escape' };
  if (ctx.typing) return null;
  if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') return { kind: 'undo' };
  if (e.metaKey || e.ctrlKey || e.altKey || !ctx.onMap) return null;
  const n = Number(e.key);
  if (n >= 1 && n <= MAX_REGION_KEY && n <= ctx.regionCount)
    return { kind: 'region', index: n - 1 };
  return letterCommand(e.key.toLowerCase(), ctx.mode);
}

function letterCommand(k: string, mode: MapMode): KeyCommand | null {
  const toMode = MODE_KEYS.get(k);
  if (toMode) return { kind: 'mode', mode: toMode };
  if (k === 'e') return { kind: 'mode', mode: mode === 'erase' ? 'view' : 'erase' };
  if (k === 'x') return mode === 'subregion' ? { kind: 'subregion-action' } : null;
  const filter = FILTER_KEYS.get(k);
  if (filter) return { kind: 'filter', filter };
  if (k === 'g') return { kind: 'grow' };
  const tool = TOOL_KEYS.get(k);
  return tool ? { kind: 'tool', tool } : null;
}
